import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { Accordion, Button, Form, OverlayTrigger, Spinner, Tab, Tabs, Tooltip } from 'react-bootstrap';
import { withTranslation, useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { RegulationContext } from '../../../App';
import AppFilter from '../../../components/AppFilter/AppFilter';
import PaginationComponent from '../../../components/Pagination/Pagination';
import {
  DataViewCategoryType,
  DEFAULT_ITEMS_PER_PAGE,
  LS_ESG_FILTER_KEY,
  StandardCategory,
} from '../../../core/constants/common';
import useHasPermission from '../../../core/hooks/useHasPermission';
import CompanyService from '../../../core/service/CompanyService';
import GeneralDisclosureService from '../../../core/service/GeneralDisclosureService';
import StandardService from '../../../core/service/StandardService';
import { getCompanyInfoFromStorage, getDefaultSelectableYear } from '../../../core/utilities/assessmentPeriodUtils';
import AITextGenWidget from '../../../components/AI/AITextGenWidget';
import esg_ai from '../../../images/esg_ai.png';
import QualitativeFilterStatus from './QualitativeFilterStatus';
import './QualitativeTableSearchPage.scss';
import './EnterpriseStructure.scss';

const readEsgFilterFromStorage = () => {
  try {
    const raw = localStorage.getItem(LS_ESG_FILTER_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};

/** Same persistence key/shape as MaterialTopic's saveESGFilterToStorage. */
const saveEsgFilterToStorage = (filterData) => {
  try {
    localStorage.setItem(LS_ESG_FILTER_KEY, JSON.stringify(filterData));
  } catch {
    // ignore storage errors, same as readEsgFilterFromStorage
  }
};

const extractItems = (res) => {
  const root = res?.data != null ? res.data : res;
  if (root == null) return [];
  if (Array.isArray(root)) return root;
  if (Array.isArray(root.content)) return root.content;
  if (Array.isArray(root.items)) return root.items;
  if (Array.isArray(root.results)) return root.results;
  if (Array.isArray(root.data)) return root.data;
  if (Array.isArray(root.records)) return root.records;
  return [];
};

/**
 * Same ordering as elsewhere (e.g. EnterpriseGroup, GeneralDisclosure): BE total from
 * `x-total-count` header first, then body totalElements.
 */
const parseTotalItemCount = (res, items, apiPage, pageSize) => {
  const headers = res?.headers ?? {};
  const fromHeader = parseFloat(headers['x-total-count']);
  if (!Number.isNaN(fromHeader) && fromHeader >= 0) {
    return fromHeader;
  }
  const root = res?.data;
  if (root && typeof root === 'object' && !Array.isArray(root)) {
    if (typeof root.totalElements === 'number') return root.totalElements;
    if (
      root.data &&
      typeof root.data === 'object' &&
      typeof root.data.totalElements === 'number'
    ) {
      return root.data.totalElements;
    }
  }
  if (items.length < pageSize) {
    return apiPage * pageSize + items.length;
  }
  return (apiPage + 1) * pageSize;
};

const pickTopicName = (item) => {
  if (item == null || typeof item !== 'object') return '';
  return (
    item.topicName ??
    item.topic?.name ??
    item.topic_name ??
    ''
  );
};

/** Disclosure search hits: group/label by reporting standard type (e.g. ESRS, GRI). */
const pickStandardType = (item) => {
  if (item == null || typeof item !== 'object') return '';
  const v =
    item.standardType ??
    item.standard_type ??
    item.standardTypeName ??
    item.standard_type_name ??
    item.disclosureStandardType ??
    item.generalDisclosureStandardType ??
    item.standard?.type ??
    item.standard?.standardType ??
    item.standard?.name ??
    '';
  return typeof v === 'string' ? v : String(v);
};

const pickQuestionText = (item) => {
  if (item == null) return '';
  if (typeof item === 'string') return item;
  return (
    item.question ??
    item.questionText ??
    item.questionName ??
    item.name ??
    ''
  );
};

/** Prefer `answers[]` row matching reporting period when API sends multiple periods (disclosure search). */
const pickAnswerText = (item, reportingPeriod = null) => {
  if (item == null || typeof item !== 'object') return '';
  if (Array.isArray(item.answers) && item.answers.length > 0) {
    if (reportingPeriod) {
      const rp = String(reportingPeriod).trim();
      const matchPeriod = item.answers.find(
        (a) => a != null && String(a.period ?? '').trim() === rp
      );
      if (matchPeriod?.answer != null && `${matchPeriod.answer}`.trim() !== '') {
        return matchPeriod.answer;
      }
    }
    if (item.answers[0]?.answer != null) return item.answers[0].answer;
  }
  return (
    item.answer ??
    item.answerText ??
    item.answerValue ??
    item.answer?.answer ??
    ''
  );
};

/** Only explicit NA/switch-off disables editing (API may omit `disabled`). */
const isSearchAnswerDisabled = (node) =>
  node?.disabled === true || node?.disabled === 'true';

/**
 * Resolve metric/disclosure question id from search API shapes (snake_case + nested).
 * Disclosure qualitative search returns the question primary key as `id` (see BRSR tree).
 * Metric search may overload `id`; only trust `id` as question id when `searchKind === 'disclosure'`.
 */
const pickQuestionId = (item, searchKind = null) => {
  if (item == null || typeof item !== 'object') return null;
  const candidates = [
    ...(searchKind === 'disclosure' ? [item.id] : []),
    item.questionId,
    item.question_id,
    item.metricQuestionId,
    item.metric_question_id,
    item.disclosureQuestionId,
    item.disclosure_question_id,
    item.generalDisclosureQuestionId,
    item.gdQuestionId,
    item.childQuestionId,
    item.subQuestionId,
    item.qnId,
    item.mqId,
    item.questionMasterId,
    item.question?.id,
    item.question?.questionId,
    item.metricQuestion?.id,
    item.metricQuestion?.questionId,
    item.metricQuestion?.metricQuestionId,
    item.metricQuestionDTO?.id,
    item.metricQuestionDto?.id,
    item.metricQuestionDTO?.questionId,
    item.metricQuestionDto?.questionId,
    item.answer?.questionId,
    item.answerDTO?.questionId,
    item.metricAnswer?.questionId,
    item.answer?.metricQuestionId,
    item.metricAnswerDTO?.questionId,
    item.disclosureQuestion?.id,
    item.gdQuestion?.id,
    item.generalDisclosureQuestion?.id,
  ];
  for (const c of candidates) {
    if (c === undefined || c === null || c === '') continue;
    const n = typeof c === 'number' ? c : Number(String(c).trim());
    if (Number.isFinite(n) && n > 0) return n;
  }
  return null;
};

/** Child rows in search sometimes omit question id; inherit from parent hit when needed. */
const pickQuestionIdForNode = (node, parentItem, searchKind = null) =>
  pickQuestionId(node, searchKind) ??
  (parentItem != null ? pickQuestionId(parentItem, searchKind) : null);

/** Same tree shape as metric catalog or General Disclosure (nested `children`). */
const flattenQuestionTree = (questions, acc = []) => {
  if (!Array.isArray(questions)) return acc;
  for (const q of questions) {
    if (q != null) {
      acc.push(q);
      if (Array.isArray(q.children) && q.children.length > 0) {
        flattenQuestionTree(q.children, acc);
      }
    }
  }
  return acc;
};

/** Response from getQuestionsAndAnswers may be array, or `{ data: [...] }` (see NewReportModal). */
const extractDisclosureQuestionListFromApi = (res) => {
  const root = res?.data;
  if (root == null) return [];
  if (Array.isArray(root)) return root;
  if (Array.isArray(root.data)) return root.data;
  if (Array.isArray(root.questions)) return root.questions;
  if (Array.isArray(root.content)) return root.content;
  return extractItems(res);
};

/**
 * Metric bulk row id — same semantics as CompanyQuestionContainer `answer.id` (0 = create).
 * Do not use root `item.id` unless it cannot equal resolved question id (search rows often mix fields).
 */
const pickMetricBulkRecordId = (node, resolvedQuestionId) => {
  if (node == null) return 0;
  const fromAnswer =
    node.answerId ??
    node.answer?.id ??
    node.metricAnswerId ??
    node.metricAnswer?.id;
  if (fromAnswer !== undefined && fromAnswer !== null && `${fromAnswer}`.trim() !== '') {
    const n = Number(fromAnswer);
    return Number.isFinite(n) ? n : 0;
  }
  const rootId = node.id;
  const qid =
    resolvedQuestionId != null ? Number(resolvedQuestionId) : NaN;
  const rid = rootId != null ? Number(rootId) : NaN;
  if (Number.isFinite(rid) && Number.isFinite(qid) && rid !== qid) {
    return rid;
  }
  return 0;
};

/**
 * Disclosure bulk row id — same as CompanyQuestionContainer (answer id, or null when new).
 * Search API uses `id` for the question; answer ids live on `answers[]` / `answer`.
 */
const pickDisclosureBulkRecordId = (node, reportingPeriod = null) => {
  if (node == null || typeof node !== 'object') return null;
  const arr = Array.isArray(node.answers) ? node.answers : [];
  if (reportingPeriod && arr.length > 0) {
    const rp = String(reportingPeriod).trim();
    const match = arr.find(
      (a) => a != null && String(a.period ?? '').trim() === rp
    );
    if (match?.id != null) {
      const n = Number(match.id);
      if (Number.isFinite(n) && n > 0) return n;
    }
  }
  const fromAnswer =
    node.answerId ??
    node.answer?.id ??
    (arr[0] != null ? arr[0].id : null);
  if (fromAnswer === undefined || fromAnswer === null || `${fromAnswer}`.trim() === '') {
    return null;
  }
  const n = Number(fromAnswer);
  return Number.isFinite(n) && n > 0 ? n : null;
};

/**
 * Same semantics as MaterialTopic / CompanyQuestionContainer: persisted `answer.cell`, else `'1'`.
 * (Utils has no `getCellValueForQuestionAnswer`; that call crashed saves.)
 */
const pickCellForBulkSave = (node) => {
  if (node == null) return '1';
  if (node.cell != null && `${node.cell}`.trim() !== '') {
    return String(node.cell).trim();
  }
  if (Array.isArray(node.answers) && node.answers.length > 0) {
    const c = node.answers[0]?.cell;
    if (c != null && `${c}`.trim() !== '') return String(c).trim();
  }
  return '1';
};

/** Stable local-edit keys: `"0"` parent row, `"0:c:1"` second child of row 0 */
const makeAnswerKey = (rowIndex, childIndex = null) =>
  childIndex == null || childIndex === undefined
    ? `${rowIndex}`
    : `${rowIndex}:c:${childIndex}`;

const parseAnswerKey = (key) => {
  const s = String(key);
  const sep = ':c:';
  const i = s.indexOf(sep);
  if (i < 0) return { rowIndex: Number(s), childIndex: null };
  return {
    rowIndex: Number(s.slice(0, i)),
    childIndex: Number(s.slice(i + sep.length)),
  };
};

const itemHasChildren = (item) =>
  Array.isArray(item?.children) && item.children.length > 0;

const pickChildQuestionText = (child) => {
  if (child == null || typeof child !== 'object') return '';
  return (
    child.question ??
    child.questionText ??
    child.questionName ??
    child.name ??
    ''
  );
};

const pickHelpTextForNode = (node, parentItem) => {
  const n = node ?? parentItem;
  if (n == null || typeof n !== 'object') return '';
  return (
    n.helpText ??
    n.help_text ??
    n.guideline ??
    n.guidance ??
    n.metricQuestion?.helpText ??
    n.question?.helpText ??
    ''
  );
};

const pickQuestionCodeForNode = (node) => {
  if (node == null || typeof node !== 'object') return '';
  const c =
    node.code ??
    node.questionCode ??
    node.metricQuestion?.code ??
    node.metricQuestion?.questionCode ??
    '';
  return typeof c === 'string' ? c : String(c);
};

/** Stable map key when API omits topic name */
const TOPIC_GROUP_UNKNOWN = '__qual_topic_unknown__';

const QualitativeTableSearchPage = ({ t }) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { glbRegulation } = useContext(RegulationContext);
  const { hasDMOneTimeUpdatePermission } = useHasPermission();

  const periodFromQuery = searchParams.get('period');
  /** Real state (not useMemo) so AppFilter's onApplyFilter can update period/category/topics in place. */
  const [filter, setFilter] = useState(() => {
    const saved = readEsgFilterFromStorage();
    const defaultEndYear = getDefaultSelectableYear(getCompanyInfoFromStorage());
    const period = periodFromQuery || saved?.reportingPeriod || `${defaultEndYear}-01-01`;
    return {
      reportingPeriod: period,
      categoryType: saved?.categoryType ?? DataViewCategoryType.ENVIRONMENTAL,
      topicIds: Array.isArray(saved?.topicIds) ? saved.topicIds : [],
    };
  });
  const [filterDisplay, setFilterDisplay] = useState(false);

  const reportingPeriod = filter.reportingPeriod;

  const [searchText, setSearchText] = useState('');
  const [searchKind, setSearchKind] = useState('metric');
  const [results, setResults] = useState([]);
  const [itemsTotalCount, setItemsTotalCount] = useState(0);
  const [itemsPerPage, setItemsPerPage] = useState(DEFAULT_ITEMS_PER_PAGE);
  const [currentPage, setCurrentPage] = useState(1);
  const [errorMessage, setErrorMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [searchAttempted, setSearchAttempted] = useState(false);
  const [localAnswers, setLocalAnswers] = useState({});
  const [savingIndex, setSavingIndex] = useState(null);
  const [topics, setTopics] = useState([]);
  /** Same shape as MaterialTopic `topicForAITextGen` (+ `answerKey` for textarea transfer). */
  const [topicForAITextGen, setTopicForAITextGen] = useState({ id: 0 });
  /** Query actually used for `results` (not live `searchText`) — keeps child-match filtering in sync with what's on screen. */
  const [lastSearchedQuery, setLastSearchedQuery] = useState('');

  const standardId = glbRegulation?.id;

  /** Cache getMetricQuestions flat lists per topic (same ids as CompanyQuestionContainer `question.id`). */
  const metricQuestionsByTopicCacheRef = useRef({});
  /** Cache flattened disclosure questions per standard type + period (General Disclosure catalog). */
  const disclosureCatalogByStdRef = useRef({});
  const { i18n } = useTranslation();

  useEffect(() => {
    metricQuestionsByTopicCacheRef.current = {};
    disclosureCatalogByStdRef.current = {};
  }, [standardId, reportingPeriod, filter.categoryType, filter.topicIds]);

  useEffect(() => {
    if (!standardId || !reportingPeriod) return;
    StandardService.getTopicsByCategoryAndStandard(
      filter.categoryType,
      standardId,
      reportingPeriod,
      StandardCategory.QUALITATIVE
    )
      .then((res) => {
        if (res.status !== 200 || !Array.isArray(res.data)) return;
        setTopics(res.data.sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0)));
      })
      .catch(() => {});
  }, [standardId, reportingPeriod, filter.categoryType]);

  useEffect(() => {
    const next = {};
    results.forEach((item, i) => {
      next[makeAnswerKey(i, null)] = pickAnswerText(item, reportingPeriod);
      if (itemHasChildren(item)) {
        item.children.forEach((ch, ci) => {
          next[makeAnswerKey(i, ci)] = pickAnswerText(ch, reportingPeriod);
        });
      }
    });
    setLocalAnswers(next);
  }, [results, reportingPeriod]);

  /**
   * A matched parent question comes back with ALL sibling children attached, not just the
   * one that matched the search text — only show the child(ren) whose question/answer text
   * actually contains the query; fall back to showing every child if none match by text
   * (e.g. the parent itself matched on code/topic rather than any child's text).
   */
  const getDisplayChildEntries = (item) => {
    const entries = (item?.children ?? []).map((child, ci) => ({ child, ci }));
    if (!lastSearchedQuery) return entries;
    const matched = entries.filter(({ child }) => {
      const questionText = (pickChildQuestionText(child) || '').toLowerCase();
      const answerText = String(
        pickAnswerText(child, reportingPeriod) ?? ''
      ).toLowerCase();
      return (
        questionText.includes(lastSearchedQuery) ||
        answerText.includes(lastSearchedQuery)
      );
    });
    return matched.length > 0 ? matched : entries;
  };

  const canSearch = useMemo(() => {
    if (!reportingPeriod) return false;
    if (searchKind === 'disclosure') return true;
    return Boolean(standardId);
  }, [reportingPeriod, searchKind, standardId]);

  /** Empty query caused backend SQL errors; never call search APIs without trimmed text. */
  const hasSearchQuery = useMemo(
    () => searchText.trim().length > 0,
    [searchText]
  );

  /**
   * Matches GeneralDisclosure: UI page is 1-based; `pageNumberFromUi === 0` means
   * items-per-page changed — reset to first page. API uses 0-based page index.
   */
  const fetchQualitativeResults = async (kind, pageNumberFromUi, itemsPerPageVal) => {
    if (!reportingPeriod) {
      setErrorMessage(t('QualitativeTableSearchPage.error.noPeriod.label'));
      return;
    }
    if (kind === 'metric' && !standardId) {
      setErrorMessage(t('QualitativeTableSearchPage.error.noStandard.label'));
      return;
    }
    const query = searchText.trim();
    if (!query) {
      setLoading(false);
      setResults([]);
      setItemsTotalCount(0);
      setSearchAttempted(false);
      setErrorMessage('');
      return;
    }
    let apiPage = 0;
    if (pageNumberFromUi > 0) {
      apiPage = pageNumberFromUi - 1;
    } else {
      setCurrentPage(1);
    }
    const perPage =
      itemsPerPageVal > 0 ? itemsPerPageVal : itemsPerPage;

    setErrorMessage('');
    setLoading(true);
    try {
      let res;
      if (kind === 'metric') {
        res = await CompanyService.searchQualitativeMetricAnswers(
          standardId,
          filter.categoryType,
          reportingPeriod,
          query,
          perPage,
          apiPage,
          filter.topicIds
        );
      } else {
        res = await CompanyService.searchQualitativeDisclosureAnswers(
          reportingPeriod,
          query,
          perPage,
          apiPage
        );
      }
      const items = extractItems(res);
      const total = parseTotalItemCount(res, items, apiPage, perPage);
      setResults(items);
      setItemsTotalCount(total);
      setSearchAttempted(true);
      setLastSearchedQuery(query.toLowerCase());
    } catch (e) {
      setErrorMessage(
        e?.response?.data?.message ??
          e?.message ??
          t('QualitativeTableSearchPage.error.generic.label')
      );
      setResults([]);
      setItemsTotalCount(0);
    } finally {
      setLoading(false);
    }
  };

  const handlePaginationClicked = (pageNum, perPageVal) => {
    fetchQualitativeResults(searchKind, pageNum, perPageVal);
  };

  const handleKindChange = (kind) => {
    setSearchKind(kind);
    setCurrentPage(1);
    fetchQualitativeResults(kind, 1, itemsPerPage);
  };

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    if (!searchText.trim()) return;
    setCurrentPage(1);
    fetchQualitativeResults(searchKind, 1, itemsPerPage);
  };

  /**
   * Metric: group by topic name (fallback: Uncategorized).
   * Disclosure: group by standardType; accordion title "Disclosures — {standardType}" (not topic fallback).
   */
  const groupedSearchResults = useMemo(() => {
    const groups = new Map();
    results.forEach((item, rowIndex) => {
      let mapKey;
      let title;

      if (searchKind === 'disclosure') {
        const st = (pickStandardType(item) ?? '').trim();
        mapKey = `disc::${st || TOPIC_GROUP_UNKNOWN}`;
        title = t('QualitativeTableSearchPage.disclosureGroupTitle.label', {
          standardType:
            st ||
            t('QualitativeTableSearchPage.disclosureGroupStandardUnknown.label'),
        });
      } else {
        const raw = (pickTopicName(item) ?? '').trim();
        mapKey = raw || TOPIC_GROUP_UNKNOWN;
        title =
          raw || t('QualitativeTableSearchPage.topicFallback.label');
      }

      if (!groups.has(mapKey)) {
        groups.set(mapKey, { title, rows: [] });
      }
      groups.get(mapKey).rows.push({ item, rowIndex });
    });
    return Array.from(groups.entries()).map(([mapKey, { title, rows }]) => ({
      mapKey,
      title,
      rows,
    }));
  }, [results, t, searchKind]);

  const openKeys = useMemo(
    () => groupedSearchResults.map((_, i) => String(i)),
    [groupedSearchResults]
  );

  const handleQualitativeTabChange = (key) => {
    if (key === 'SUPPLEMENTARY') {
      navigate('/company_admin/qualitative');
    }
  };

  const handleAnswerChange = (answerKey, value) => {
    setLocalAnswers((prev) => ({ ...prev, [answerKey]: value }));
  };

  /**
   * When search hits omit question ids, resolve `question.id` like CompanyQuestionContainer via
   * getMetricQuestions(topic) and match code or question text (including child questions).
   */
  const resolveMetricQuestionIdFromCatalog = async (
    node,
    parentItem,
    fromChildRow
  ) => {
    if (!standardId || !reportingPeriod) return null;

    const topicIdRaw =
      node?.topicId ??
      parentItem?.topicId ??
      node?.topic?.id ??
      parentItem?.topic?.id ??
      (Array.isArray(filter.topicIds) && filter.topicIds.length === 1
        ? filter.topicIds[0]
        : null);

    if (topicIdRaw == null || topicIdRaw === '') return null;

    const topicId =
      typeof topicIdRaw === 'number'
        ? topicIdRaw
        : Number(String(topicIdRaw).trim());
    if (!Number.isFinite(topicId)) return null;

    const cacheKey = `${standardId}|${reportingPeriod}|${filter.categoryType}|${topicId}`;
    let flat = metricQuestionsByTopicCacheRef.current[cacheKey];
    if (!flat) {
      try {
        const res = await CompanyService.getMetricQuestions(
          standardId,
          reportingPeriod,
          filter.categoryType,
          topicId
        );
        const qs = res?.data?.questions ?? [];
        flat = flattenQuestionTree(qs, []);
        metricQuestionsByTopicCacheRef.current[cacheKey] = flat;
      } catch {
        return null;
      }
    }

    const nodeCode =
      node?.code ??
      node?.questionCode ??
      node?.metricQuestion?.code ??
      node?.metricQuestion?.questionCode;
    if (nodeCode != null && `${nodeCode}`.trim() !== '') {
      const codeStr = String(nodeCode).trim();
      const byCode = flat.find((q) => String(q.code ?? '').trim() === codeStr);
      if (byCode?.id != null) return Number(byCode.id);
    }

    const text = (
      fromChildRow ? pickChildQuestionText(node) : pickQuestionText(node)
    ).trim();
    if (text) {
      const byText = flat.find((q) => (q.question || '').trim() === text);
      if (byText?.id != null) return Number(byText.id);
    }

    const tryIds = [node?.id, parentItem?.id];
    for (const raw of tryIds) {
      if (raw == null) continue;
      const n = Number(raw);
      if (!Number.isFinite(n)) continue;
      const byId = flat.find((q) => Number(q.id) === n);
      if (byId?.id != null) return Number(byId.id);
    }

    return null;
  };

  /**
   * When disclosure search hits omit question ids, resolve `question.id` via
   * getQuestionsAndAnswers only (same catalog source as elsewhere; no paged GET).
   */
  const resolveDisclosureQuestionIdFromCatalog = async (
    node,
    parentItem,
    fromChildRow
  ) => {
    if (!reportingPeriod) return null;

    const stdType = (
      pickStandardType(node) ||
      pickStandardType(parentItem) ||
      ''
    ).trim();
    if (!stdType) return null;

    const lang = (i18n?.language || 'en').split('-')[0];
    const cacheKey = `${stdType}|${reportingPeriod}|${lang}`;
    let flat = disclosureCatalogByStdRef.current[cacheKey];
    if (!flat) {
      let list = [];
      try {
        const res = await GeneralDisclosureService.getQuestionsAndAnswers({
          stdType,
          period: reportingPeriod,
          lang,
        });
        list = extractDisclosureQuestionListFromApi(res);
      } catch {
        list = [];
      }
      flat = flattenQuestionTree(Array.isArray(list) ? list : [], []);
      disclosureCatalogByStdRef.current[cacheKey] = flat;
    }

    const nodeCode =
      node?.code ??
      node?.questionCode ??
      node?.disclosureQuestion?.code ??
      node?.gdQuestion?.code;
    if (nodeCode != null && `${nodeCode}`.trim() !== '') {
      const codeStr = String(nodeCode).trim();
      const byCode = flat.find(
        (q) => String(q.code ?? '').trim() === codeStr
      );
      if (byCode?.id != null) return Number(byCode.id);
    }

    const text = (
      fromChildRow ? pickChildQuestionText(node) : pickQuestionText(node)
    ).trim();
    if (text) {
      const byText = flat.find((q) => {
        const qn = (q.name || q.question || '').trim();
        return qn === text;
      });
      if (byText?.id != null) return Number(byText.id);
    }

    const tryIds = [node?.id, parentItem?.id];
    for (const raw of tryIds) {
      if (raw == null) continue;
      const n = Number(raw);
      if (!Number.isFinite(n)) continue;
      const byId = flat.find((q) => Number(q.id) === n);
      if (byId?.id != null) return Number(byId.id);
    }

    return null;
  };

  const saveAnswerAtIndex = async (answerKey) => {
    const { rowIndex, childIndex } = parseAnswerKey(answerKey);
    const item = results[rowIndex];
    if (!item) return;
    const node =
      childIndex != null ? item.children?.[childIndex] : item;
    if (!node) return;
    let questionId =
      childIndex != null
        ? pickQuestionIdForNode(node, item, searchKind)
        : pickQuestionId(node, searchKind);

    if (questionId == null && searchKind === 'metric') {
      questionId = await resolveMetricQuestionIdFromCatalog(
        node,
        item,
        childIndex != null
      );
    }
    if (questionId == null && searchKind === 'disclosure') {
      questionId = await resolveDisclosureQuestionIdFromCatalog(
        node,
        item,
        childIndex != null
      );
    }

    if (questionId == null) {
      toast.error(t('QualitativeTableSearchPage.save.errorNoQuestionId.label'));
      return;
    }
    const answerText = localAnswers[answerKey] ?? '';
    /** Mirrors CompanyQuestionContainer.saveIndividualAnswer payload shape. */
    const payload = [
      {
        id:
          searchKind === 'metric'
            ? pickMetricBulkRecordId(node, questionId)
            : pickDisclosureBulkRecordId(node, reportingPeriod),
        questionId: +questionId,
        answer: answerText,
        cell: pickCellForBulkSave(node),
      },
    ];
    setSavingIndex(answerKey);
    try {
      if (searchKind === 'metric') {
        await CompanyService.postMetricBulkAnswerForSearch(reportingPeriod, payload, {
          topicIds: filter.topicIds,
        });
      } else {
        const stdType = (
          pickStandardType(node) ||
          pickStandardType(item) ||
          ''
        ).trim();
        await GeneralDisclosureService.saveDisclosureBulkAnswer(
          reportingPeriod,
          payload,
          stdType ? { standardType: stdType } : null
        );
      }
      toast.success(t('QualitativeTableSearchPage.save.success.label'));
    } catch (e) {
      const detail =
        e?.response?.data?.message ??
        e?.response?.data?.detail ??
        e?.message;
      toast.error(
        typeof detail === 'string' && detail.trim()
          ? detail
          : t('QualitativeTableSearchPage.save.error.label')
      );
    } finally {
      setSavingIndex(null);
    }
  };

  const openRefineWithAiForAnswerKey = async (answerKey) => {
    if (searchKind !== 'metric') return;
    const { rowIndex, childIndex } = parseAnswerKey(answerKey);
    const item = results[rowIndex];
    if (!item) return;
    const node =
      childIndex != null ? item.children?.[childIndex] : item;
    if (!node) return;

    let questionId =
      childIndex != null
        ? pickQuestionIdForNode(node, item, searchKind)
        : pickQuestionId(node, searchKind);

    if (questionId == null) {
      questionId = await resolveMetricQuestionIdFromCatalog(
        node,
        item,
        childIndex != null
      );
    }

    if (questionId == null || !Number.isFinite(Number(questionId)) || Number(questionId) <= 0) {
      toast.error(t('QualitativeTableSearchPage.save.errorNoQuestionId.label'));
      return;
    }

    const qid = Number(questionId);
    const questionText =
      childIndex != null
        ? pickChildQuestionText(node)
        : pickQuestionText(node);
    const topicLabel = (pickTopicName(item) ?? '').trim();
    const answerText = localAnswers[answerKey] ?? '';

    setTopicForAITextGen({
      id: qid,
      answerKey,
      materialtopic:
        topicLabel ||
        t('QualitativeTableSearchPage.topicFallback.label'),
      question: questionText,
      guideline: String(pickHelpTextForNode(node, item) ?? ''),
      answer: answerText,
      questionCode: pickQuestionCodeForNode(node),
    });
  };

  return (
    <div className="QualitativeTableSearchPage MaterialTopic" data-testid="qualitative-table-search-page">
      <div className="List_header">
        <div>
          <h5 className="MH">{t('OneTime.label')}</h5>
        </div>
      </div>
      <div className="MaterialTopic_tabbox">
        <Tabs
          activeKey="ESG"
          id="qualitative_table_search_tabs"
          className="mb-0"
          onSelect={handleQualitativeTabChange}
        >
          <Tab eventKey="ESG" title={t('ESGNarratives.label')} />
          {/* <Tab eventKey="SUPPLEMENTARY" title={t('SupplementaryInformation.label')} /> */}
        </Tabs>
        <div className="Onetime_filter">
          <QualitativeFilterStatus
            filter={filter}
            topics={topics}
            onFilterClick={() => setFilterDisplay(!filterDisplay)}
            showViewToggle
            initialViewMode="table"
            onTableViewNavigate={() => {}}
            onListViewNavigate={() => navigate('/company_admin/qualitative')}
          />
        </div>
        {filterDisplay && (
          <AppFilter
            type={'NR_ESG'}
            existingFilter={{ period: filter.reportingPeriod }}
            esgFilter={filter}
            onApplyFilter={(f) => {
              setTopics(f.topicsFromFilter);
              const newFilter = {
                reportingPeriod: f.period,
                categoryType: f.categoryType,
                topicIds: f.selectedTopicIds,
              };
              setFilter(newFilter);
              saveEsgFilterToStorage(newFilter);
            }}
            onFilterHide={(value) => setFilterDisplay(value)}
          />
        )}
        <div className="tab-content">
          <div className="tab-pane active">
            <div className="row m-0">
              <div
                className={`p-0 ${
                  topicForAITextGen.id > 0 ? 'col-md-8' : 'col-md-12'
                }`}
              >
                <div
                  className="qualitative-table-search__page-inner"
                  data-testid="qualitative-table-search-inner"
                >
                  {/* <div
                    className="qualitative-table-search__intro"
                    role="note"
                    data-testid="qualitative-table-search-intro"
                  >
                    {t('ESRInfo.label')}
                  </div> */}

                  <div className="qualitative-table-search qualitative-table-search--surface card">
                    <div className="card-body qualitative-table-search__card-body">
                      <Form
                        onSubmit={handleSearchSubmit}
                        className="qualitative-table-search__form"
                      >
                        <div className="qualitative-table-search__toolbar">
                          <Form.Group className="qualitative-table-search__search-group mb-0">
                            <Form.Label
                              htmlFor="qualitative-table-search-input"
                              className="qualitative-table-search__field-label"
                            >
                              {t('QualitativeTableSearchPage.search.label')}
                            </Form.Label>
                            <div className="qualitative-table-search__search-row">
                              <Form.Control
                                id="qualitative-table-search-input"
                                type="search"
                                value={searchText}
                                onChange={(e) => setSearchText(e.target.value)}
                                placeholder={t(
                                  'QualitativeTableSearchPage.search.placeholder.label'
                                )}
                                autoComplete="off"
                                disabled={!canSearch}
                              />
                            </div>
                          </Form.Group>

                          <Form.Group className="qualitative-table-search__radios mb-0">
                            <Form.Label className="qualitative-table-search__field-label">
                              {t('QualitativeTableSearchPage.source.label')}
                            </Form.Label>
                            <div
                              className="qualitative-table-search__source-segment"
                              role="radiogroup"
                              aria-label={t('QualitativeTableSearchPage.source.label')}
                            >
                              <Form.Check
                                type="radio"
                                name="qualitativeSearchKind"
                                id="qualitativeSearchMetric"
                                label={t('QualitativeTableSearchPage.radio.metric.label')}
                                checked={searchKind === 'metric'}
                                onChange={() => handleKindChange('metric')}
                                disabled={!reportingPeriod || loading}
                              />
                              <Form.Check
                                type="radio"
                                name="qualitativeSearchKind"
                                id="qualitativeSearchDisclosure"
                                label={t(
                                  'QualitativeTableSearchPage.radio.disclosure.label'
                                )}
                                checked={searchKind === 'disclosure'}
                                onChange={() => handleKindChange('disclosure')}
                                disabled={!reportingPeriod || loading}
                              />
                            </div>
                          </Form.Group>

                          <div className="qualitative-table-search__submit-group">
                            <span
                              className="qualitative-table-search__field-label qualitative-table-search__field-label--spacer"
                              aria-hidden="true"
                            >
                              {'\u00a0'}
                            </span>
                            <Button
                              type="submit"
                              variant="outline-primary"
                              className="qualitative-table-search__submit-btn"
                              disabled={!canSearch || loading || !hasSearchQuery}
                            >
                              {loading ? (
                                <>
                                  <Spinner
                                    animation="border"
                                    size="sm"
                                    className="me-2"
                                    aria-hidden={true}
                                  />
                                  {t('QualitativeTableSearchPage.searching.label')}
                                </>
                              ) : (
                                t('QualitativeTableSearchPage.search.button.label')
                              )}
                            </Button>
                          </div>
                        </div>
                      </Form>

                      {searchKind === 'metric' && !standardId && (
                        <p className="qualitative-table-search__hint qualitative-table-search__hint--warn mb-0">
                          {t('QualitativeTableSearchPage.error.noStandard.label')}
                        </p>
                      )}

                      {errorMessage && (
                        <div
                          className="alert alert-danger qualitative-table-search__alert mb-0"
                          role="alert"
                        >
                          {errorMessage}
                        </div>
                      )}

                      {!searchAttempted && !errorMessage && (
                        <div className="qualitative-table-search__callout" data-testid="qualitative-search-instructions">
                          {t('QualitativeTableSearchPage.instructions.label')}
                        </div>
                      )}

                      {results.length === 0 &&
                        searchAttempted &&
                        !loading &&
                        !errorMessage && (
                          <div className="qualitative-table-search__empty-state">
                            {t('QualitativeTableSearchPage.empty.label')}
                          </div>
                        )}

                      {results.length > 0 && (
                        <Accordion
                          key={`qual-acc-${currentPage}-${searchKind}-${results.length}-${groupedSearchResults.length}`}
                          alwaysOpen
                          defaultActiveKey={openKeys}
                          className="qualitative-table-search__accordion qualitative-table-search__accordion--material-topic qualitative-table-search__results-accordion"
                        >
                          {groupedSearchResults.map((group, gIndex) => (
                            <Accordion.Item
                              eventKey={String(gIndex)}
                              key={`topic-grp-${currentPage}-${searchKind}-${gIndex}-${group.mapKey}`}
                            >
                              <Accordion.Header>
                                {group.title}
                              </Accordion.Header>
                              <Accordion.Body className="qualitative-table-search__accordion-body">
                                {group.rows.map(({ item, rowIndex }, idxInGroup) => {
                                  const hasCh = itemHasChildren(item);
                                  const rootKey = makeAnswerKey(rowIndex, null);
                                  const isLastInTopic =
                                    idxInGroup === group.rows.length - 1;

                                  return (
                                    <div
                                      key={`row-${currentPage}-${rowIndex}`}
                                      className={
                                        isLastInTopic
                                          ? 'qualitative-table-search__topic-group-question'
                                          : 'qualitative-table-search__topic-group-question qualitative-table-search__topic-group-question--divider'
                                      }
                                    >
                                      <div className="qualitative-table-search__result qualitative-table-search__result-body metriTopicQus-container">
                                        {hasCh ? (
                                          <>
                                            <div className="qualitative-table-search__question-block">
                                              <h6 className="qualitative-table-search__question-title mb-0">
                                                {pickQuestionText(item) || '—'}
                                              </h6>
                                            </div>
                                            <div className="qualitative-table-search__children-wrap childrenQuestions_box">
                                              {getDisplayChildEntries(item).map(({ child, ci }) => {
                                                const childKey = makeAnswerKey(rowIndex, ci);
                                                return (
                                                  <div
                                                    key={child.id ?? childKey}
                                                    className="qualitative-table-search__child-block"
                                                  >
                                                    <div className="qualitative-table-search__question-block qualitative-table-search__question-block--child qualitative-table-search__child-question">
                                                      <h6 className="qualitative-table-search__question-title qualitative-table-search__question-title--sub mb-0">
                                                        {pickChildQuestionText(child) || '—'}
                                                      </h6>
                                                    </div>
                                                    <div
                                                      id={`qual-search-ans-${childKey}`}
                                                      className="qualitative-table-search__answer-panel answer-container"
                                                    >
                                                      <textarea
                                                        id={`qual-search-answer-${childKey}`}
                                                        rows={6}
                                                        className="form-control editable qualitative-table-search__answer-input"
                                                        value={localAnswers[childKey] ?? ''}
                                                        onChange={(e) =>
                                                          handleAnswerChange(
                                                            childKey,
                                                            e.target.value
                                                          )
                                                        }
                                                        disabled={
                                                          isSearchAnswerDisabled(child) ||
                                                          savingIndex === childKey ||
                                                          !hasDMOneTimeUpdatePermission()
                                                        }
                                                      />
                                                      <div className="qualitative-table-search__answer-footer">
                                                        {searchKind === 'metric' && (
                                                          <OverlayTrigger
                                                            placement="top"
                                                            overlay={
                                                              <Tooltip id={`tooltip-esg-ai-${childKey}`}>
                                                                {t(
                                                                  'RefineYourResponseESGAICopilotToolTip.label'
                                                                )}
                                                              </Tooltip>
                                                            }
                                                          >
                                                            <button
                                                              type="button"
                                                              className="btn qualitative-table-search__copilot-btn btn-ESGAi"
                                                              data-testid={`qual-search-esg-ai-${childKey}`}
                                                              hidden={!hasDMOneTimeUpdatePermission()}
                                                              onClick={() =>
                                                                openRefineWithAiForAnswerKey(
                                                                  childKey
                                                                )
                                                              }
                                                            >
                                                              <img
                                                                src={esg_ai}
                                                                width={15}
                                                                height={15}
                                                                alt=""
                                                                className="qualitative-table-search__copilot-icon"
                                                              />
                                                              {t('ESGAICopilot.label')}
                                                            </button>
                                                          </OverlayTrigger>
                                                        )}
                                                        {!isSearchAnswerDisabled(child) && (
                                                          <button
                                                            type="button"
                                                            className="btn btn-primary qualitative-table-search__save-btn"
                                                            hidden={
                                                              !hasDMOneTimeUpdatePermission()
                                                            }
                                                            disabled={
                                                              savingIndex === childKey
                                                            }
                                                            onClick={() =>
                                                              saveAnswerAtIndex(childKey)
                                                            }
                                                          >
                                                            {savingIndex === childKey ? (
                                                              <>
                                                                <Spinner
                                                                  animation="border"
                                                                  size="sm"
                                                                  className="me-2"
                                                                  aria-hidden={true}
                                                                />
                                                                {t(
                                                                  'QualitativeTableSearchPage.saving.label'
                                                                )}
                                                              </>
                                                            ) : (
                                                              t('Save.label')
                                                            )}
                                                          </button>
                                                        )}
                                                      </div>
                                                    </div>
                                                  </div>
                                                );
                                              })}
                                            </div>
                                          </>
                                        ) : (
                                          <>
                                            <div className="qualitative-table-search__question-block">
                                              <h6 className="qualitative-table-search__question-title mb-0">
                                                {pickQuestionText(item) || '—'}
                                              </h6>
                                            </div>
                                            <div
                                              id={`qual-search-ans-${rootKey}`}
                                              className="qualitative-table-search__answer-panel answer-container"
                                            >
                                              <textarea
                                                id={`qual-search-answer-${rootKey}`}
                                                rows={6}
                                                className="form-control editable qualitative-table-search__answer-input"
                                                value={localAnswers[rootKey] ?? ''}
                                                onChange={(e) =>
                                                  handleAnswerChange(
                                                    rootKey,
                                                    e.target.value
                                                  )
                                                }
                                                disabled={
                                                  isSearchAnswerDisabled(item) ||
                                                  savingIndex === rootKey ||
                                                  !hasDMOneTimeUpdatePermission()
                                                }
                                              />
                                              <div className="qualitative-table-search__answer-footer">
                                                {searchKind === 'metric' && (
                                                  <OverlayTrigger
                                                    placement="top"
                                                    overlay={
                                                      <Tooltip id={`tooltip-esg-ai-${rootKey}`}>
                                                        {t(
                                                          'RefineYourResponseESGAICopilotToolTip.label'
                                                        )}
                                                      </Tooltip>
                                                    }
                                                  >
                                                    <button
                                                      type="button"
                                                      className="btn qualitative-table-search__copilot-btn btn-ESGAi"
                                                      data-testid={`qual-search-esg-ai-${rootKey}`}
                                                      hidden={!hasDMOneTimeUpdatePermission()}
                                                      onClick={() =>
                                                        openRefineWithAiForAnswerKey(rootKey)
                                                      }
                                                    >
                                                      <img
                                                        src={esg_ai}
                                                        width={15}
                                                        height={15}
                                                        alt=""
                                                        className="qualitative-table-search__copilot-icon"
                                                      />
                                                      {t('ESGAICopilot.label')}
                                                    </button>
                                                  </OverlayTrigger>
                                                )}
                                                {!isSearchAnswerDisabled(item) && (
                                                  <button
                                                    type="button"
                                                    className="btn btn-primary qualitative-table-search__save-btn"
                                                    hidden={
                                                      !hasDMOneTimeUpdatePermission()
                                                    }
                                                    disabled={savingIndex === rootKey}
                                                    onClick={() =>
                                                      saveAnswerAtIndex(rootKey)
                                                    }
                                                  >
                                                    {savingIndex === rootKey ? (
                                                      <>
                                                        <Spinner
                                                          animation="border"
                                                          size="sm"
                                                          className="me-2"
                                                          aria-hidden={true}
                                                        />
                                                        {t(
                                                          'QualitativeTableSearchPage.saving.label'
                                                        )}
                                                      </>
                                                    ) : (
                                                      t('Save.label')
                                                    )}
                                                  </button>
                                                )}
                                              </div>
                                            </div>
                                          </>
                                        )}
                                      </div>
                                    </div>
                                  );
                                })}
                              </Accordion.Body>
                            </Accordion.Item>
                          ))}
                        </Accordion>
                      )}

                      {searchAttempted && !errorMessage && results.length > 0 && (
                        <div className="qualitative-table-search__pagination-footer List_footer_count">
                          <PaginationComponent
                            itemsTotalCount={itemsTotalCount}
                            itemsPerPage={itemsPerPage}
                            currentPage={currentPage}
                            setCurrentPage={setCurrentPage}
                            setItemsPerPage={setItemsPerPage}
                            alwaysShown
                            pageClicked={handlePaginationClicked}
                          />
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
              {topicForAITextGen?.id > 0 && (
                <div className="col-md-4 p-2">
                  <AITextGenWidget
                    type="MATERIAL_TOPIC"
                    materialTopic={topicForAITextGen}
                    onHide={() => setTopicForAITextGen({ id: 0 })}
                    onTransferToEditor={(text) => {
                      const key = topicForAITextGen.answerKey;
                      if (key) {
                        setLocalAnswers((prev) => ({
                          ...prev,
                          [key]: text,
                        }));
                      }
                      setTopicForAITextGen({ id: 0 });
                    }}
                  />
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default withTranslation()(QualitativeTableSearchPage);
